/**
 * Pure view helpers for the Connection Health Settings tab. Kept framework-free
 * (no React) so the chain-ordering, status mapping, and formatting logic can be
 * unit-tested directly — the components below are thin renderers over these.
 *
 * The chain order mirrors the Design §9 localization matrix
 * (network → gateway → OPC → device → DB → remote gateway) so a failure's
 * position in the data path is visually obvious when the checks are laid out
 * per gateway.
 */

import type {
  BackfillConnectionHealthChecksResult,
  ConnectionHealthStatus,
  ConnectionHealthStatusRow
} from '@axilon-apps/domains-ontology-ui-state'

import type { Status } from '../../../components/primitives/types'

/**
 * Rank of each check kind along the data-access chain. Lower sorts first.
 * Several OPC / DB kinds share a rank because they probe the same hop from
 * different angles; ties break on target then check_kind for stable output.
 */
const CHAIN_RANK: Record<string, number> = {
  gateway_http_ping: 0, // gateway reachability
  opc_endpoint_ping: 1, // OPC
  opc_reading_mode: 1,
  opc_server_status_tag: 1,
  device_status: 2, // device
  db_ping_webdev: 3, // DB
  db_status_ignition_tag: 3,
  db_connection_mode: 3,
  gateway_network_status: 4 // remote gateway peers
}

const UNRANKED = 99

const CHECK_KIND_LABEL: Record<string, string> = {
  gateway_http_ping: 'Gateway HTTP',
  opc_endpoint_ping: 'OPC endpoint',
  opc_reading_mode: 'OPC reading mode',
  opc_server_status_tag: 'OPC server status',
  device_status: 'Device',
  db_ping_webdev: 'Database ping',
  db_status_ignition_tag: 'Database status',
  db_connection_mode: 'Database mode',
  gateway_network_status: 'Gateway network peer'
}

const STATUS_LABEL: Record<ConnectionHealthStatus, string> = {
  up: 'Up',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'Unknown'
}

const STATUS_PILL: Record<ConnectionHealthStatus, Status> = {
  up: 'run',
  degraded: 'warn',
  down: 'down',
  unknown: 'idle'
}

/** Chain rank for a check kind; unknown kinds sort last. */
export function checkKindRank(kind: string): number {
  return CHAIN_RANK[kind] ?? UNRANKED
}

/** Human-friendly label for a check kind, humanizing unknown kinds. */
export function checkKindLabel(kind: string): string {
  const known = CHECK_KIND_LABEL[kind]
  if (known) return known
  return kind
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** Display label for a health status. */
export function statusLabel(status: ConnectionHealthStatus): string {
  return STATUS_LABEL[status] ?? 'Unknown'
}

/** Map a health status onto a StatusPill tone. */
export function statusToPill(status: ConnectionHealthStatus): Status {
  return STATUS_PILL[status] ?? 'idle'
}

/**
 * Chain-order comparator for two status rows: by chain rank, then target, then
 * check kind. Stable for equal rows.
 */
export function compareStatusRows(
  a: ConnectionHealthStatusRow,
  b: ConnectionHealthStatusRow
): number {
  const rank = checkKindRank(a.check_kind) - checkKindRank(b.check_kind)
  if (rank !== 0) return rank
  const target = a.target.localeCompare(b.target)
  if (target !== 0) return target
  return a.check_kind.localeCompare(b.check_kind)
}

export interface GatewayGroup {
  gatewayUrl: string
  rows: ConnectionHealthStatusRow[]
}

/**
 * Group status rows by gateway (sorted by gateway URL) with each group's rows
 * laid out in chain order. Does not mutate the input.
 */
export function groupRowsByGateway(rows: ConnectionHealthStatusRow[]): GatewayGroup[] {
  const byGateway = new Map<string, ConnectionHealthStatusRow[]>()
  for (const row of rows) {
    const list = byGateway.get(row.gateway_url)
    if (list) {
      list.push(row)
    } else {
      byGateway.set(row.gateway_url, [row])
    }
  }
  return [...byGateway.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([gatewayUrl, groupRows]) => ({
      gatewayUrl,
      rows: [...groupRows].sort(compareStatusRows)
    }))
}

/** Format a latency in ms, or an em dash when absent. */
export function formatLatency(latencyMs?: number): string {
  if (typeof latencyMs !== 'number' || !Number.isFinite(latencyMs)) return '—'
  return `${Math.round(latencyMs)} ms`
}

/**
 * Format the age of the latest reading (staleness) as a compact relative
 * string. `undefined` (a check with no reading yet) renders "No data".
 */
export function formatAge(ageMs?: number): string {
  if (typeof ageMs !== 'number' || !Number.isFinite(ageMs)) return 'No data'
  const ms = Math.max(0, ageMs)
  if (ms < 1000) return 'just now'
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

/** Operator-facing summary of a check-backfill run. */
export function formatBackfillSummary(result: BackfillConnectionHealthChecksResult): string {
  if (result.gateways === 0) {
    return 'No gateways found. Ingest a gateway backup or register WebDev credentials first.'
  }
  const gatewayWord = result.gateways === 1 ? 'gateway' : 'gateways'
  if (result.failed > 0) {
    return `Provisioned ${result.upserted} of ${result.attempted} checks across ${result.gateways} ${gatewayWord}. ${result.failed} failed.`
  }
  return `Provisioned ${result.upserted} checks across ${result.gateways} ${gatewayWord}.`
}

/** Short display name for a gateway URL (host[:port]), falling back to the URL. */
export function gatewayLabel(url: string): string {
  try {
    const parsed = new URL(url)
    return parsed.host || url
  } catch {
    return url
  }
}
